import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import cone from '../assets/da2026/cone.png';

// Les horaires sont stockes en texte « HH:MM ». On les convertit en minutes plutot
// que de trier lexicalement : le zero initial n'est garanti par rien en base, et un
// « 9:00 » non padde se retrouverait apres « 18:00 ».
const enMinutes = (heure) => {
  const m = /^\s*(\d{1,2})\s*[:hH]\s*(\d{2})\s*$/.exec(heure || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.MAX_SAFE_INTEGER;
};

function Terrains() {
  const [terrains, setTerrains] = useState([]);
  const [matchs, setMatchs] = useState([]);
  const [terrainActif, setTerrainActif] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followedTeam] = useState(() => {
    try { return localStorage.getItem('abrf_followed_team') || ''; } catch (e) { return ''; }
  });

  useEffect(() => {
    const charger = async () => {
      try {
        setLoading(true);

        // Meme repli singulier/pluriel que le reste de l'appli sur la table des tournois.
        let tournoisRes = await supabase.from('tournoi').select('*').order('ordre');
        if (tournoisRes.error) {
          tournoisRes = await supabase.from('tournois').select('*').order('ordre');
        }

        const [terrainsRes, matchsRes, equipesRes, phasesRes] = await Promise.all([
          supabase.from('terrain').select('*'),
          supabase.from('match').select('*'),
          supabase.from('equipe').select('id, nom'),
          supabase.from('phase').select('*'),
        ]);

        if (terrainsRes.error) throw terrainsRes.error;
        if (matchsRes.error) throw matchsRes.error;
        if (tournoisRes.error) throw tournoisRes.error;

        const nomEquipe = {};
        (equipesRes.data || []).forEach(e => { nomEquipe[e.id] = e.nom; });

        const nomPhase = {};
        (phasesRes.data || []).forEach(p => { nomPhase[p.id] = p.nom; });

        // L'ordre des journees se deduit de `ordre`, qui suit la chronologie du
        // festival : on prend le plus petit ordre parmi les tournois d'une meme
        // journee. Rien n'est code en dur, une edition qui ajouterait un jeudi se
        // classerait toute seule.
        const ordreJour = {};
        const tournoiParId = {};
        (tournoisRes.data || []).forEach((t, index) => {
          const ordre = t.ordre != null ? t.ordre : index;
          const nom = t.nom || t.name || `Tournoi ${t.id}`;
          // Sans `jour` renseigne, le tournoi forme sa propre section plutot que de
          // se melanger a une journee dont on ne sait rien.
          const jour = (t.jour || '').trim() || nom;
          tournoiParId[t.id] = { nom, jour, ordre };
          if (ordreJour[jour] === undefined || ordre < ordreJour[jour]) ordreJour[jour] = ordre;
        });

        const nomTerrain = {};
        (terrainsRes.data || []).forEach(t => {
          // La colonne en base s'appelle `terrain`, pas `nom`.
          nomTerrain[t.id] = t.terrain || t.nom || `Terrain ${t.id}`;
        });

        // Un match sans equipe1_texte est un emplacement de tableau encore vide :
        // meme filtre que la fiche tournoi, il n'a rien a faire dans un planning.
        const prepares = (matchsRes.data || [])
          .filter(m => m.equipe1_texte && String(m.equipe1_texte).trim() !== '')
          .filter(m => m.terrain_id != null)
          .map(m => {
            const t = tournoiParId[m.tournoi_id] || { nom: '', jour: '', ordre: 9999 };
            return {
              id: m.id,
              terrainId: m.terrain_id,
              heure: m.heure || '',
              equipe1: nomEquipe[m.equipe1_id] || m.equipe1_texte,
              equipe2: nomEquipe[m.equipe2_id] || m.equipe2_texte,
              score1: m.score_equipe1,
              score2: m.score_equipe2,
              tournoiId: m.tournoi_id,
              tournoiNom: t.nom,
              jour: t.jour,
              jourOrdre: ordreJour[t.jour] !== undefined ? ordreJour[t.jour] : 9999,
              phaseNom: nomPhase[m.phase_id] || '',
            };
          })
          // A l'interieur d'une journee, tous les matchs tombent le meme jour :
          // l'heure suffit, et les tournois d'une meme journee s'entrelacent comme
          // ils s'enchainent reellement sur le terrain.
          .sort((a, b) => (a.jourOrdre - b.jourOrdre) || (enMinutes(a.heure) - enMinutes(b.heure)));

        const terrainsUtilises = (terrainsRes.data || [])
          .map(t => ({ id: t.id, nom: nomTerrain[t.id] }))
          .filter(t => prepares.some(m => m.terrainId === t.id))
          .sort((a, b) => a.nom.localeCompare(b.nom, 'fr', { numeric: true }));

        setTerrains(terrainsUtilises);
        setMatchs(prepares);
        setTerrainActif(terrainsUtilises.length ? terrainsUtilises[0].id : null);
      } catch (e) {
        console.error('Erreur lors du chargement de la vision par terrain:', e);
        setError(e.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    charger();
  }, []);

  // Matchs du terrain courant, decoupes en journees.
  const journees = useMemo(() => {
    const blocs = [];
    matchs
      .filter(m => m.terrainId === terrainActif)
      .forEach(m => {
        const dernier = blocs[blocs.length - 1];
        if (dernier && dernier.jour === m.jour) dernier.matchs.push(m);
        else blocs.push({ jour: m.jour, matchs: [m] });
      });
    return blocs;
  }, [matchs, terrainActif]);

  const estSuivie = (nom) => followedTeam && nom && nom.toLowerCase() === followedTeam.toLowerCase();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-10">
        <div className="card p-6 text-center">
          <h2 className="h-display text-xl text-brand-ink mb-2">Oups…</h2>
          <p className="text-sm text-brand-muted mb-4">Impossible de charger les matchs : {error}</p>
          <button onClick={() => window.location.reload()} className="btn-primary mt-2">
            <i className="ri-refresh-line"></i> Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pb-20">
      {/* ===== EN-TÊTE ===== */}
      <header className="relative bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white rounded-b-3xl overflow-hidden shadow-hero px-5 pt-8 pb-6 mb-4">
        <div className="absolute top-0 inset-x-0 h-2 hazard-white"></div>
        <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/85">
          <i className="ri-arrow-left-s-line text-base"></i> Accueil
        </Link>
        <h1 className="h-display text-4xl mt-2 leading-[0.95]">Vision<br />par terrain</h1>
        <p className="text-sm text-white/90 mt-3 max-w-[16rem]">
          Tous les matchs d'un terrain, dans l'ordre de la journée.
        </p>
        <img src={cone} alt="" aria-hidden="true" className="absolute right-4 bottom-3 w-16 opacity-95 drop-shadow-lg" />
      </header>

      {terrains.length === 0 ? (
        <div className="px-5">
          <div className="card p-8 text-center flex flex-col items-center">
            <img src={cone} alt="" className="h-20 w-auto mb-4" />
            <h2 className="h-display text-xl mb-2 text-brand-ink">Aucun match programmé</h2>
            <p className="text-sm text-brand-muted">
              Les rencontres apparaîtront ici dès que les terrains seront attribués.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* ===== SÉLECTEUR DE TERRAIN ===== */}
          <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-brand-line px-5 py-3">
            <div className="flex gap-2 overflow-x-auto no-sb">
              {terrains.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTerrainActif(t.id)}
                  className={`shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full border whitespace-nowrap transition-colors ${
                    terrainActif === t.id
                      ? 'bg-brand-ink text-white border-brand-ink'
                      : 'bg-white text-brand-muted border-brand-line'
                  }`}
                >
                  {t.nom}
                </button>
              ))}
            </div>
          </div>

          {/* ===== PLANNING ===== */}
          <div className="px-5 pt-4 flex flex-col gap-6">
            {journees.length === 0 ? (
              <div className="card p-6 text-center">
                <p className="text-brand-muted text-sm">Aucun match sur ce terrain.</p>
              </div>
            ) : (
              journees.map((journee, i) => (
                <section key={`${journee.jour}-${i}`}>
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <h2 className="h-display text-lg text-brand-ink leading-tight">{journee.jour}</h2>
                    <span className="text-[11px] font-semibold text-brand-muted">
                      {journee.matchs.length} match{journee.matchs.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2">
                    {journee.matchs.map(m => {
                      const joue = m.score1 != null && m.score2 != null;
                      const gagnant = joue
                        ? (Number(m.score1) > Number(m.score2) ? 1 : Number(m.score2) > Number(m.score1) ? 2 : 0)
                        : null;
                      return (
                        <div key={m.id} className="flex items-stretch gap-2.5">
                          <div
                            className={`w-[52px] shrink-0 rounded-xl flex flex-col items-center justify-center ${
                              joue ? 'bg-[#F7F1EB] text-brand-muted' : 'bg-brand-orange-soft text-brand-orange-deep'
                            }`}
                          >
                            <span className="font-display text-[15px] leading-none">{m.heure || '—'}</span>
                          </div>

                          <div className="flex-1 min-w-0 rounded-2xl border border-brand-line bg-white px-3.5 py-2.5 shadow-soft">
                            {/* Une journee peut accueillir deux tournois : sans ce rappel,
                                impossible de savoir a quelle competition tient un match. */}
                            <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
                              <Link
                                to={`/tournament/${m.tournoiId}`}
                                className="text-[10px] font-bold uppercase tracking-wider text-brand-orange-deep truncate"
                              >
                                {m.tournoiNom}
                              </Link>
                              {m.phaseNom && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted shrink-0">
                                  · {m.phaseNom}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2.5">
                              <span
                                className={`flex-1 min-w-0 font-bold text-sm flex items-center gap-1 ${
                                  gagnant === 1 ? 'text-brand-orange-deep' : 'text-brand-ink'
                                }`}
                              >
                                {estSuivie(m.equipe1) && <i className="ri-star-fill text-brand-orange text-xs shrink-0"></i>}
                                <span className="truncate">{m.equipe1}</span>
                              </span>

                              {joue ? (
                                <span className="shrink-0 font-display text-base text-brand-orange-deep bg-brand-orange-soft rounded-lg px-2 py-1">
                                  {m.score1} – {m.score2}
                                </span>
                              ) : (
                                <span className="shrink-0 font-display text-xs text-brand-muted bg-[#F7F1EB] rounded-lg px-2 py-1">VS</span>
                              )}

                              <span
                                className={`flex-1 min-w-0 font-bold text-sm flex items-center justify-end gap-1 text-right ${
                                  gagnant === 2 ? 'text-brand-orange-deep' : 'text-brand-ink'
                                }`}
                              >
                                <span className="truncate">{m.equipe2}</span>
                                {estSuivie(m.equipe2) && <i className="ri-star-fill text-brand-orange text-xs shrink-0"></i>}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Terrains;
